import { React, ReactDOM, Button } from 'uweb'
import { THREE, ThreeView } from 'uweb/three'
import { MapView, maptalks } from 'uweb/maptalks'
import { Point, UTM, colorize } from 'uweb/utils'
import { KeyValue, Safe, Loop, Delay } from 'utils/web'

import { CSS3DRenderer, CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js'

type Status = 'idle' | 'loading' | 'success' | 'error'

const STATUS_COLOR: Record<Status, string> = {
    idle: '#3a4750',
    loading: '#3ab0ff',
    success: '#3aff8a',
    error: '#ff4a4a',
}

const STATUS_LABEL: Record<Status, string> = {
    idle: 'IDLE', loading: 'LOADING', success: 'OK', error: 'ERROR',
}

type Theme = {
    bezel: string         // outer panel background
    screen: string        // inner screen background
    text: string          // body text
    titleText: string     // dimmed label text
    scanline: string      // subtle horizontal lines
    outline: string       // hard black/white rim around the whole panel
    innerEdge: string     // faint highlight along inner screen edge
    pillBg: (a: string) => string   // status pill fill given accent
    pillBorder: string    // status pill border (uses accent in both themes)
}

const THEMES: Record<'dark' | 'light', Theme> = {
    dark: {
        bezel: 'linear-gradient(180deg, #1a1d22 0%, #0c0e11 100%)',
        screen: '#06080a',
        text: '#dfe7ee',
        titleText: '#cfdce6',
        scanline: '#ffffff05',
        outline: '#000',
        innerEdge: '#ffffff08',
        pillBg: a => `${a}22`,
        pillBorder: '',   // accent
    },
    light: {
        bezel: 'linear-gradient(180deg, #f5f7fa 0%, #d8dde4 100%)',
        screen: '#fafcfe',
        text: '#1a232b',
        titleText: '#2c3a45',
        scanline: '#0000000a',
        outline: '#6a7480',     // soft outline instead of pitch black on light
        innerEdge: '#00000010',
        pillBg: a => `${a}1f`,
        pillBorder: '',
    },
}

const LedPanel = ({
    status = 'idle',
    title,
    isDarkMode = true,
    children,
}: {
    status?: Status
    title?: string
    isDarkMode?: boolean
    children?: React.ReactNode
}) => {
    const accent = STATUS_COLOR[status]
    const t = isDarkMode ? THEMES.dark : THEMES.light

    return (
        <>
            <style>{`
                @keyframes led-shimmer { 0% { transform: translateX(-100%) } 100% { transform: translateX(100%) } }
                @keyframes led-glow    { 0%,100% { box-shadow: 0 0 0 3px ${t.outline}, 0 0 16px ${accent}66 } 50% { box-shadow: 0 0 0 3px ${t.outline}, 0 0 40px ${accent}cc } }
                @keyframes led-pulse   { 0%,100% { opacity: 0.6 } 50% { opacity: 1 } }
                @keyframes led-scan    { to { background-position: 0 -200px } }
            `}</style>

            <div style={{
                position: 'relative',
                width: '100%', height: '100%',
                boxSizing: 'border-box',
                padding: 10,
                borderRadius: 14,
                background: t.bezel,
                boxShadow: `0 0 0 3px ${t.outline}, 0 0 24px ${accent}66`,
                animation: status === 'loading' ? 'led-glow 1.6s ease-in-out infinite' : undefined,
                fontFamily: 'system-ui, sans-serif',
                color: t.text,
                overflow: 'hidden',
            }}>

                <div style={{
                    position: 'relative',
                    width: '100%', height: '100%',
                    boxSizing: 'border-box',
                    borderRadius: 8,
                    background: t.screen,
                    boxShadow: `inset 0 0 60px ${accent}22, inset 0 0 0 2px ${accent}`,
                    overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>

                    {status === 'loading' && (
                        <div style={{
                            position: 'relative',
                            height: '8%', minHeight: 24,
                            background: `${accent}22`,
                            overflow: 'hidden',
                            flexShrink: 0,
                        }}>
                            <div style={{
                                position: 'absolute', top: 0, bottom: 0, left: 0,
                                width: '40%',
                                background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                                boxShadow: `0 0 20px ${accent}`,
                                animation: 'led-shimmer 1.4s ease-in-out infinite',
                            }} />
                        </div>
                    )}

                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: `repeating-linear-gradient(0deg, ${t.scanline} 0 1px, transparent 1px 3px)`,
                        animation: 'led-scan 4s linear infinite',
                        pointerEvents: 'none',
                    }} />

                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                        padding: '12px 16px',
                        borderBottom: `2px solid ${accent}55`,
                        position: 'relative', zIndex: 1,
                        flexShrink: 0,
                    }}>
                        <div style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            fontSize: 14, letterSpacing: 2, textTransform: 'uppercase',
                            color: t.titleText,
                        }}>
                            <span style={{
                                width: 14, height: 14, borderRadius: '50%',
                                background: accent,
                                boxShadow: `0 0 12px ${accent}, 0 0 24px ${accent}88`,
                                animation: status === 'loading' ? 'led-pulse 1s ease-in-out infinite' : undefined,
                            }} />
                            {title}
                        </div>

                        <div style={{
                            padding: '4px 10px',
                            borderRadius: 4,
                            background: t.pillBg(accent),
                            border: `1px solid ${accent}`,
                            color: accent,
                            fontSize: 12, letterSpacing: 1.5, fontWeight: 600,
                            textShadow: `0 0 8px ${accent}${isDarkMode ? '' : '00'}`,   // drop glow in light
                        }}>
                            {STATUS_LABEL[status]}
                        </div>
                    </div>

                    <div style={{ flex: 1, padding: 16, position: 'relative', zIndex: 1, overflow: 'auto' }}>
                        {children}
                    </div>
                </div>
            </div>
        </>
    )
}

export class Init_CSS3DRenderer {

    cfg: iArgs
    cssRenderer: any
    cssScene = new THREE.Scene()

    constructor(cfg: iArgs) {

        this.cfg = cfg
        // in prepareToDraw, after the map exists:
        const c = cfg.MapView?.map.getContainer()
        this.cssRenderer = new CSS3DRenderer()
        this.cssRenderer.setSize(c.clientWidth, c.clientHeight)
        this.cssRenderer.domElement.style.cssText = 'position:absolute;top:0;left:0;pointer-events:none'
        c.appendChild(this.cssRenderer.domElement)
        cfg.MapView?.map.on('resize', () => this.cssRenderer.setSize(c.clientWidth, c.clientHeight))

        const updater = this.cfg.MapView?.conf
        updater.postUpdateCallback = (...e: any) => {
            this.cssRenderer.render(this.cssScene, cfg.MapView?.camera)
        }

    }

    addTV = (cornerWorld: any) => {

        const host = document.createElement('div')
        host.style.cssText = 'width:800px;height:600px;pointer-events:auto;'
        host.onpointerdown = e => e.stopPropagation()
        const root = ReactDOM.createRoot(host)
        root.render(
            <LedPanel status="loading" title="Survey Telemetry" isDarkMode={this.cfg.isDarkMode}>
                <div>Cut: 56,216 m³</div>
                <div>Fill: 0 m³</div>
                <Button>OKLA</Button>
            </LedPanel>
        )

        const unitsPerMeter = this.cfg.MapView?.threeLayer.distanceToVector3(1, 1).x

        // unitsPerMeter - 1m
        // currentZ      - ?m

        const tv = new CSS3DObject(host)
        tv.position.copy(cornerWorld)                          // world position
        tv.scale.setScalar(unitsPerMeter * 2 / 320)            // 2 m wide TV (320px host)
        tv.quaternion.copy(this.cfg.MapView!.camera.quaternion) // billboard once

        const deg45 = Math.PI / 4
        tv.rotateX(Math.PI + deg45)
        tv.rotateY(Math.PI)
        tv.rotateZ(Math.PI)

        // console.log(unitsPerMeter)
        // console.log(cornerWorld.z / unitsPerMeter + 1500)

        this.cssScene.add(tv)

    }

}

